/**
 * Implements a binance datafeed
 * See:
 * https://medium.com/marcius-studio/connecting-and-settings-tradingview-with-js-api-and-udf-adapter-b790297a31fa
 */
import { CexAPI, KattanaAPI, subscription } from '../../../store/createStore';
import { ErrorCallback,
  HistoryCallback,
  IDatafeedChartApi,
  LibrarySymbolInfo,
  PeriodParams,
  ResolutionString,
  SearchSymbolsCallback,
  SubscribeBarsCallback
} from '../charting_library/datafeed-api';
import eventEmitter from '../../../core/eventEmitter';
import { helpers, ITicker } from '@kattana-io/common';

const supported_resolutions = [
  '1', '3', '5', '15', '30', '60', '120', '240', '360', '480', '720', '1d', '3d', '1w', '1M'
];

class BinanceDatafeed implements IDatafeedChartApi {
    debug: boolean;
    timeframe: helpers.resolutinFormatter.EBinanceTimeframes | undefined;
    lastPriceFn: () => void;
    timedSubscriptionsMap: any;
    liveSubscriptionsMap: any;
    exchange: string;
    ticker: ITicker | null;
    lastBar: any;
    onRealtimeCallback?: SubscribeBarsCallback;
    cexPriceEventName: string | undefined;

    constructor(options: any) {
      this.debug = options.debug || false;
      /**
         * Realtime candles
         */
      this.lastPriceFn = () => { };
      this.timedSubscriptionsMap = {};
      this.liveSubscriptionsMap = {};
      this.exchange = helpers.exchanges.BINANCE;
      // this.klines = [];

      this.ticker = null;
    }

    public isIntervalSupported = (interval: string): boolean =>
      supported_resolutions.includes(interval);

    binanceKlines = async (symbol: string, interval: string, startTime: number, endTime: number, limit: number) => {
      const sym = symbol.split('/').join('');
      const timeframe = helpers.resolutinFormatter.binanceResolutionFormatter(String(interval));
      this.timeframe = timeframe;

      const data = await CexAPI.fetchKLines(this.exchange, sym, timeframe, startTime, endTime, limit);
      return data;
    }

    getLastKline = async (symbol: string, interval: string) => {
      const sym = symbol.split('/').join('');

      const [data] = await CexAPI.fetchLastKLine(this.exchange, sym, interval);

      if (data) {
        this.lastBar = this.klineToCandle(data);
      }

      return this.lastBar;
    }

    onReady = (callback: Function) => {
      try {
        setTimeout(() => {
          callback({
            supports_marks: false,
            supports_timescale_marks: false,
            supports_time: true,
            supported_resolutions,
          });
        }, 0);

      } catch (err) {
        console.error(err);
      }
    }

    /**
    * Request remote symbols
    */
    findSymbols = async (symbolId: number, query: string) => {
      const tickers = await KattanaAPI.tickers({exchange: helpers.exchanges.BINANCE, searchRaw: query, id: symbolId});
      if (!tickers) {
        return [];
      }
      const symbols: any[] = tickers.map((item: ITicker) => {
        const symbol = item.name;
        const [base_asset, quote_asset] = symbol.split('/');

        return ({
          symbol: symbol.toUpperCase(),
          symbolName: `${base_asset}${quote_asset}`.toUpperCase(), // well, this is how we get input
          base_asset,
          quote_asset,
          base_address: item.base_address,
          quote_address: item.quote_address,
          base_decimals: item.base_decimals,
          quote_decimals: item.quote_decimals,
          precision: item.quote_decimals,
          ticker: item,
          price: item.price_a,
        });
      });

      return symbols;
    }
    searchSymbols = async (
      userInput: string,
      exchange: string,
      symbolType: string,
      onResultReadyCallback: SearchSymbolsCallback
    ) => {
      const input = userInput.toUpperCase();
      await this.findSymbols(-1, input).then((symbols: any) => {
        onResultReadyCallback(symbols
          .map((symbol: any) => ({
            symbol: symbol.symbol,
            full_name: symbol.symbol,
            description: `${symbol.base_asset} / ${symbol.quote_asset}`,
            pair: `${symbol.quote_asset}-${symbol.base_asset}`,
            ticker: symbol.ticker,
            exchange: this.exchange,
            type: 'stock'
          }),
          ));
      });
    }

    resolveSymbol = async (symbolName: string, onSymbolResolvedCallback: Function, onResolveErrorCallback: (val: string) => void) => {
      if (this.debug) {console.log('👉 resolveSymbol:', symbolName);}
      const searchById = symbolName.includes('{id}:');

      const comps = symbolName.split(':');
      // eslint-disable-next-line no-param-reassign
      let symbolId = -1;
      if (searchById) {
        symbolId = Number(comps[1]);
      }

      symbolName = (comps.length > 1 ? comps[1] : symbolName).toUpperCase();
      const [symbol] = await this.findSymbols(symbolId, symbolName);

      if (symbol) {
        if (symbol.ticker) {
          this.ticker = symbol.ticker;
          this.cexPriceEventName = `cex_price_${this.ticker?.id}`;
        }

        onSymbolResolvedCallback({
          name: symbol.symbol,
          description: `${symbol.base_asset} / ${symbol.quote_asset}`,
          pair: `${symbol.base_asset}/${symbol.quote_asset}`,
          ticker: symbol.symbol,
          session: '24x7',
          minmov: 1,
          pricescale: 10 ** symbol.base_decimals,
          timezone: 'UTC',
          has_intraday: true,
          has_daily: true,
          has_weekly_and_monthly: true,
          currency_code: symbol.quoteAsset,
          data: symbol,
          exchange: this.exchange
        });
      } else {
        onResolveErrorCallback('Not found');
      }
    }

    /**
     * Convert Binance's kline to candle
     * @param {*} kline
     */
    klineToCandle = (kline: any) => ({
      time: kline[0],
      close: parseFloat(kline[4]),
      open: parseFloat(kline[1]),
      high: parseFloat(kline[2]),
      low: parseFloat(kline[3]),
      volume: parseFloat(kline[5])
    })

    getBars(symbolInfo: LibrarySymbolInfo, resolution: ResolutionString, periodParams: PeriodParams, onResult: HistoryCallback, onError: ErrorCallback) {
      if (this.debug) {
        console.log('👉 getBars:', symbolInfo.name, resolution);
        console.log('First:', periodParams.firstDataRequest);
        console.log('From:', periodParams.from, (new Date(periodParams.from * 1000) as any).toGMTString());
        console.log('To:  ', periodParams.to, (new Date(periodParams.to * 1000) as any).toGMTString());
      }

      if (!resolution) {
        onError('Invalid interval');
      }

      let totalKlines: any = [];

      const finishKlines = () => {
        if (this.debug) {
          console.log('📊:', totalKlines.length);
        }

        if (totalKlines.length === 0) {
          onResult([], {
            noData: true
          });
        } else {
          const result = totalKlines.map(this.klineToCandle);
          onResult(result, { noData: false });
        }
      };

      // eslint-disable-next-line no-shadow
      const getKlines = (from: number, to: number) => {
        this.binanceKlines(symbolInfo.name, resolution, from, to, 500).then(klines => {
          totalKlines = totalKlines.concat(klines);
          if (klines.length === 500) {
            // eslint-disable-next-line no-param-reassign
            from = klines[klines.length - 1][0] + 1;
            getKlines(from, to);
          } else {
            finishKlines();
          }
          return null;
        }).catch(err => {
          console.error(err);
          onError('Some problem');
        });
      };

      // eslint-disable-next-line no-param-reassign
      periodParams.from *= 1000;
      // eslint-disable-next-line no-param-reassign
      periodParams.to *= 1000;

      getKlines(periodParams.from, periodParams.to);
    }

    // handle price on last kandle
    onPriceChange = (evdata: { price: number }) => {
      if (this.lastBar && this.onRealtimeCallback && this.timeframe) {
        const multiplier = 1;
        const newTime = helpers.datafeedCommom.formatDateByTimeframe(
          new Date().getTime(),
          this.timeframe, multiplier
        );

        let newLastBar;
        const price = evdata.price;

        if (newTime > this.lastBar.time) {
          // new candle
          newLastBar = {
            close: price,
            high: price,
            low: price,
            open: price,
            volume: 0,
          };

          // for being sure that candles are combined
          this.onRealtimeCallback({...this.lastBar, close: price});
        } else {
          // update last candle
          newLastBar = {
            ...this.lastBar,
            high: Math.max(this.lastBar.high, price),
            low: Math.min(this.lastBar.low, price),
            close: price,
          };
        }

        newLastBar.time = newTime;
        if (newTime >= this.lastBar.time) {
          this.onRealtimeCallback({...newLastBar});
          this.lastBar = newLastBar;
        }
      }
    }

    subscribeBars = async (
      symbolInfo: LibrarySymbolInfo,
      resolution: ResolutionString,
      onRealtimeCallback: SubscribeBarsCallback,
      subscriberUID: string,
      onResetCacheNeededCallback: () => void
    ) => {
      this.onRealtimeCallback = onRealtimeCallback;
      if (this.debug) {console.log('👉 subscribeBars:', subscriberUID);}

      const interval = helpers.datafeedCommom.mapInterval(resolution);
      await this.getLastKline(symbolInfo.name, interval);

      // Tick subscription
      if (this.cexPriceEventName) {
        this.liveSubscriptionsMap[subscriberUID] = subscription.subscribeCexLastPrice(this.cexPriceEventName);
        eventEmitter.on(this.cexPriceEventName, this.onPriceChange);
      }
    }

    unsubscribeBars(subscriberUID: string) {
      if (this.debug) {console.log('👉 unsubscribeBars:', subscriberUID);}

      // clear subscription from eventEmitter
      if (this.cexPriceEventName) {
        eventEmitter.removeListener(this.cexPriceEventName, this.onPriceChange);
      }

      // clear subscription from centrifuge
      if (this.liveSubscriptionsMap[subscriberUID]) {
        this.liveSubscriptionsMap[subscriberUID]();
        delete this.liveSubscriptionsMap[subscriberUID];
      }
    }

    public getTicker = () => this.ticker;
}

export default BinanceDatafeed;
